// Public, no-login guide for designers — explains the Amplify module end to end.
// Self-contained styling so it renders identically regardless of app theme.
export default function DesignerGuide() {
  return (
    <div className="dg-root">
      <style>{CSS}</style>

      <header className="dg-hero">
        <div className="dg-hero-inner">
          <div className="dg-kicker">ASKREX · AMPLIFY</div>
          <h1>The Designer's Guide to Amplify</h1>
          <p className="dg-lede">
            Amplify turns a raw idea into a finished, on-brand graphic — social posts and full
            print-ready covers alike. This guide is a complete tour of the toolkit: planning work on
            the <strong>board</strong>, building images in <strong>Compose Studio</strong>, fine-tuning
            every layer in the <strong>Director</strong>, designing <strong>covers</strong>, and the
            brand &amp; quality safeguards that keep it all consistent.
          </p>
          <div className="dg-hero-meta">A complete walkthrough · ~12 min read</div>
        </div>
      </header>

      <div className="dg-shell">
        <nav className="dg-toc">
          <div className="dg-toc-title">On this page</div>
          <a href="#overview">1 · The big picture</a>
          <a href="#board">2 · The Planner board</a>
          <a href="#card">3 · Inside a card</a>
          <a href="#studio">4 · Compose Studio</a>
          <a href="#director">5 · The Director editor</a>
          <a href="#covers">6 · Covers &amp; the Cover Library</a>
          <a href="#assets">7 · Brand assets &amp; library</a>
          <a href="#quality">8 · Brand &amp; quality safeguards</a>
          <a href="#workflow">9 · A typical workflow</a>
          <a href="#tips">10 · Tips &amp; gotchas</a>
        </nav>

        <main className="dg-main">

          {/* 1 */}
          <section id="overview" className="dg-section">
            <h2>1 · The big picture</h2>
            <p>
              Every piece of content lives as a <span className="dg-term">card</span>. A card holds the
              copy (headline, sub-header, caption), the artwork, its provenance, and its place in the
              production pipeline. The designer's job is to turn the copy + brief into a polished image.
            </p>
            <p>There are <strong>two ways to build a card's image</strong>, and you move freely between them:</p>
            <div className="dg-cards">
              <div className="dg-feature">
                <div className="dg-feature-tag">Guided</div>
                <h3>Compose Studio</h3>
                <p>
                  A step-by-step image builder: pick a layout, drop in a background, add one or more
                  cut-out subjects, recolour and stack them. The fastest route to a strong, on-brand base image.
                </p>
              </div>
              <div className="dg-feature">
                <div className="dg-feature-tag dg-feature-tag--alt">Freeform</div>
                <h3>The Director</h3>
                <p>
                  A live canvas where you place and restyle every element — text, logos, the subject,
                  crop, overlay tint, duotone — with an instant preview, then export the final sizes.
                </p>
              </div>
            </div>
            <div className="dg-callout">
              <strong>The hand-off:</strong> build the base in Compose Studio, hit
              <em> "Save to Director"</em>, and the background + subjects flow into the Director as live,
              editable layers. You then add words and brand marks on top. Nothing is "baked" until you export.
            </div>
          </section>

          {/* 2 */}
          <section id="board" className="dg-section">
            <h2>2 · The Planner board</h2>
            <p>
              Amplify opens on the <span className="dg-term">Planner</span> — a kanban board of cards
              organised by production stage:
            </p>
            <ul className="dg-pipeline">
              <li><b>Idea Stage</b> — captured ideas, not yet briefed</li>
              <li><b>Ready for Design</b> — copy is set, waiting for artwork</li>
              <li><b>In Design</b> — actively being built</li>
              <li><b>Awaiting Approval</b> — finished, sent for sign-off</li>
              <li><b>Completed</b> — approved &amp; scheduled / shipped</li>
            </ul>
            <p>The same cards can be viewed through different lenses using the tabs:</p>
            <ul className="dg-list">
              <li><b>Production</b> — the stage-based board (above)</li>
              <li><b>Week / Calendar</b> — the same cards laid out by date</li>
              <li><b>Completed</b> — an archive of shipped work</li>
            </ul>
            <p>
              Filters at the top narrow by <b>Voice</b> (whose perspective the post is written in) and
              <b> Priority</b> (P1–P4), and a <b>search</b> box matches on title or body. Click any card chip to open it.
            </p>
            <p className="dg-note">
              A card chip shows its title, how long it's been in stage, its voice, content type
              (e.g. <code>SOCIAL_POST</code>, <code>COVER_DESIGN</code>), and — once it has artwork — a
              provenance stamp: <b>REX Designed</b> for graphics built in the studio/editor, or
              <b> Uploaded</b> for graphics brought in manually.
            </p>
          </section>

          {/* 3 */}
          <section id="card" className="dg-section">
            <h2>3 · Inside a card</h2>
            <p>Clicking a card opens its detail drawer. From top to bottom you'll find:</p>
            <div className="dg-rows">
              <div className="dg-row"><div className="dg-row-h">Graphic copy</div><div>The <b>Headline</b> and <b>Sub-header</b> that appear on the artwork. Keep these tight — they drive the layout.</div></div>
              <div className="dg-row"><div className="dg-row-h">Post caption</div><div>The longer caption for the social post body. "Copy clean caption" gives you a ready-to-paste version.</div></div>
              <div className="dg-row"><div className="dg-row-h">Refine with Rex</div><div>An AI assistant — ask it to rewrite or sharpen the copy (e.g. "make this more celebratory"). It saves the new body automatically and stays on-brand (it only references RESILIGENT's own tools — DISC Lens Africa, Leadership Mirror, JobFit, InsightEdge, Leadership Labs — plus the Leadership Circle Profile).</div></div>
              <div className="dg-row"><div className="dg-row-h">Editorial &amp; VLM Critic</div><div>Automated quality checks on tone/register and on the rendered image, so problems are caught before approval.</div></div>
              <div className="dg-row"><div className="dg-row-h">Brand Manual check</div><div>Pick a <b>Voice</b> + <b>Market</b> and run a check against the brand rules — capitalisation, anti-drift, cultural do-nots and the "Do Not Use" list.</div></div>
              <div className="dg-row"><div className="dg-row-h">Hero image &amp; provenance</div><div>The artwork itself, stamped <b>REX Designed</b> or <b>Uploaded</b>. This is where you enter <b>Compose Studio</b> or the <b>Director</b>, upload a graphic, or attach one by URL.</div></div>
            </div>
          </section>

          {/* 4 */}
          <section id="studio" className="dg-section">
            <h2>4 · Compose Studio</h2>
            <p>
              Compose Studio is the guided image builder. It focuses on one job:
              <strong> make a strong base image</strong> (background + subjects). Text and logos come later, in the Director.
            </p>

            <h3 className="dg-sub">Step 1 — Choose a layout</h3>
            <p>
              Pick from on-brand layouts (Hero Campaign, Subject on Background, Photo with Text Block,
              Celebration with Portrait, and more). The layout sets where the background and subjects sit,
              over the RESILIGENT brand background where relevant.
            </p>

            <h3 className="dg-sub">Step 2 — Background &amp; subjects</h3>
            <p>Each image slot can be filled four ways:</p>
            <ul className="dg-list">
              <li><b>Library</b> — reusable, saved-once backgrounds shared across every card. <b>Rename</b> or <b>delete</b> any item right here.</li>
              <li><b>Stock</b> — search Unsplash / Pexels and drop a photo straight in.</li>
              <li><b>Abstract</b> — instant on-brand abstract backgrounds, or generate an <b>AI</b> one with the sparkle button.</li>
              <li><b>Upload</b> — bring your own file, including <b>vector (SVG)</b> art.</li>
            </ul>
            <p>
              Two shortcuts: <b>Shuffle on-brand background</b> rotates through curated brand backgrounds,
              and <b>Save this background to the library</b> adds your choice to the shared pool so it's one click away on every future card.
            </p>

            <h3 className="dg-sub">Subjects (cut-outs) — up to five layers</h3>
            <p>
              Drop a photo into a subject slot and Amplify automatically <b>removes its background</b>,
              leaving a clean cut-out. You can stack <b>up to five subjects</b> in one composition, and control each:
            </p>
            <ul className="dg-list">
              <li><b>Subject look</b> — recolour the cut-out: <em>Original</em>, <em>B&amp;W</em>, <em>Duotone</em> (brand navy→blue), or <em>Brand</em> tint.</li>
              <li><b>Drag to reposition</b> — grab a subject on the live preview and move it anywhere.</li>
              <li><b>Resize</b> — drag the corner handle, or use the size slider.</li>
              <li><b>Nudge pad</b> — arrow buttons for pixel-precise moves.</li>
              <li><b>Layer order (z-order)</b> — bring a subject forward or send it back so subjects overlap correctly.</li>
              <li><b>Drop shadow</b> — toggle a soft shadow per subject to lift it off the background.</li>
              <li><b>Vector aware</b> — SVG art stays crisp and skips the cut-out step.</li>
              <li><b>Rule of thirds</b> — flip the "Thirds" toggle for a grid; subjects snap to the thirds lines as you drag.</li>
            </ul>

            <h3 className="dg-sub">Step 3 — Save to Director</h3>
            <p>
              When the image looks right, hit <b>Save to Director</b>. Amplify flattens the background into a
              plate, keeps each subject as a <b>separate movable layer</b>, and drops you into the Director to
              add text and logos. (This takes a few seconds while the cut-outs are processed server-side.)
            </p>
            <div className="dg-callout dg-callout--warn">
              Your existing text &amp; logos are never lost in the hand-off — only the background / subjects are updated.
            </div>
          </section>

          {/* 5 */}
          <section id="director" className="dg-section">
            <h2>5 · The Director editor</h2>
            <p>
              The Director is the live canvas. The image you built becomes the base "plate" and you layer
              everything else on top. The preview is <b>instant</b> — typing, dragging and restyling update on
              screen immediately while your work saves quietly in the background (a small status shows
              <em> Saving… / Saved</em>). The finished, pixel-accurate render swaps in moments later.
            </p>

            <h3 className="dg-sub">Text &amp; typography</h3>
            <ul className="dg-list">
              <li><b>Headline &amp; sub-header</b> — drag them anywhere on the preview, or anchor them (top/bottom · left/right/centre) with pixel nudges.</li>
              <li><b>Full type control</b> — font, size, weight, italics, colour, letter-spacing, line-spacing and text transform (UPPER / lower / Title).</li>
              <li><b>Text box</b> — sit text on a panel in <em>solid</em>, <em>semi</em>, <em>glass</em> (blur) or <em>soft</em> styles, with adjustable colour, opacity, radius and padding.</li>
              <li><b>Shadow &amp; stroke</b> — add a drop shadow and/or an outline stroke for legibility on busy photos.</li>
              <li><b>Independent sub-header</b> — detach the sub-header and place it separately (handy on covers).</li>
              <li><b>On-canvas safety</b> — dragged text is kept within the frame so it can't slide off the artwork.</li>
            </ul>

            <h3 className="dg-sub">Logos, marks &amp; tagline</h3>
            <ul className="dg-list">
              <li><b>Brand logo</b> — choose a variant, position, size, opacity and an optional drop shadow.</li>
              <li><b>Client logo</b> — add a partner / client mark with its own position, size and opacity.</li>
              <li><b>Tagline &amp; contact</b> — show the brand tagline and contact line where you want them.</li>
            </ul>

            <h3 className="dg-sub">Image &amp; subjects</h3>
            <ul className="dg-list">
              <li><b>Movable subjects</b> — the cut-outs from Compose Studio stay editable: drag the "Subject" box, resize from the corner, use the size slider / nudge pad, reorder, or hide one entirely.</li>
              <li><b>Crop</b> — non-destructive crop of the photo plate.</li>
              <li><b>Overlay tint</b> — a brand colour wash (left-gradient or full) to seat text over imagery.</li>
              <li><b>Duotone</b> — a two-tone brand treatment of the background for a striking, consistent look.</li>
            </ul>

            <h3 className="dg-sub">Cover extras &amp; export</h3>
            <ul className="dg-list">
              <li><b>Cover meta block</b> — date, prepared-by and a custom note, positioned and styled for covers.</li>
              <li><b>Multi-format export</b> — render the finished card across the sizes you actually use (square, story, A4 cover, etc.) and download them.</li>
            </ul>
            <p className="dg-note">
              Everything you change is saved to the card automatically. Reopen the card later and your
              placements, styles and crop are exactly as you left them — and the graphic is stamped <b>REX Designed</b>.
            </p>
          </section>

          {/* 6 */}
          <section id="covers" className="dg-section">
            <h2>6 · Covers &amp; the Cover Library</h2>
            <p>
              Beyond social posts, Amplify designs full <span className="dg-term">covers</span> — A4,
              print-ready, at 300 DPI — for proposals, reports and decks. Covers use the same Director
              toolkit (with cover-specific touches like the meta block and an independent sub-header) and
              export to PNG and PDF.
            </p>
            <h3 className="dg-sub">The Cover Library</h3>
            <ul className="dg-list">
              <li><b>Generated runs</b> — every batch of AI-generated cover backgrounds, with front / back variants you can preview, download or send to the board.</li>
              <li><b>Saved backgrounds</b> — a curated pool of reusable cover backgrounds you can upload to, filter and manage.</li>
              <li><b>Edit on board</b> — turn any variant or saved background into a live cover card and open it straight in the Director.</li>
              <li><b>Template A composer</b> — overlay the RESILIGENT logo, title, subtitle and footer band onto a chosen background to produce a finished cover in one step.</li>
            </ul>
          </section>

          {/* 7 */}
          <section id="assets" className="dg-section">
            <h2>7 · Brand assets &amp; library</h2>
            <p>Alongside the Planner, Amplify carries the brand toolkit:</p>
            <ul className="dg-list">
              <li><b>Library</b> — finished covers and previously built artwork.</li>
              <li><b>Brand Assets</b> — logos, backgrounds and other reusable pieces, organised into pools.</li>
              <li><b>Brand Kit</b> — the colours, type and rules that keep everything consistent.</li>
            </ul>
            <p>
              Backgrounds you "Save to library" from Compose Studio live in the shared <b>backgrounds</b>
              pool — available on every card and manageable (rename / delete) from the Studio's Library tab.
            </p>
          </section>

          {/* 8 */}
          <section id="quality" className="dg-section">
            <h2>8 · Brand &amp; quality safeguards</h2>
            <p>A few systems work in the background to keep output on-brand and high-quality:</p>
            <div className="dg-rows">
              <div className="dg-row"><div className="dg-row-h">Provenance stamps</div><div>Every graphic is tagged <b>REX Designed</b> (built in the studio / editor) or <b>Uploaded</b> (brought in manually), shown on the card chip and in the drawer.</div></div>
              <div className="dg-row"><div className="dg-row-h">Brand Manual check</div><div>Validates copy against capitalisation, anti-drift, cultural do-nots and the "Do Not Use" list for the chosen voice + market.</div></div>
              <div className="dg-row"><div className="dg-row-h">Editorial critic</div><div>Reviews tone and register so copy reads the way the chosen voice should sound.</div></div>
              <div className="dg-row"><div className="dg-row-h">VLM image critic</div><div>Looks at the rendered graphic and flags issues (e.g. unreadable text, ragged cut-out edges) before sign-off.</div></div>
              <div className="dg-row"><div className="dg-row-h">On-brand tooling references</div><div>Rex only references RESILIGENT's proprietary tools (DISC Lens Africa, Leadership Mirror, JobFit, InsightEdge, Leadership Labs); the single permitted external tool is the Leadership Circle Profile.</div></div>
            </div>
          </section>

          {/* 9 */}
          <section id="workflow" className="dg-section">
            <h2>9 · A typical workflow</h2>
            <ol className="dg-steps">
              <li><b>Open a card</b> in "Ready for Design" from the Planner.</li>
              <li><b>Read the copy</b> — headline, sub-header, caption. Run the Brand Manual check if unsure, or sharpen it with Refine with Rex.</li>
              <li><b>Open Compose Studio</b> and pick a layout that fits the message.</li>
              <li><b>Set the background</b> — Library, Stock, Abstract or Upload. Save a good one to the library for reuse.</li>
              <li><b>Add subjects</b> — let them cut out, recolour, position, size, reorder and shadow them (use Thirds to frame).</li>
              <li><b>Save to Director.</b></li>
              <li><b>In the Director</b>, place and style the headline + sub-header, drop in logos and tagline, nudge the subjects, add a tint or duotone for contrast — all with the instant preview.</li>
              <li><b>Export</b> the sizes you need and move the card to "Awaiting Approval".</li>
            </ol>
          </section>

          {/* 10 */}
          <section id="tips" className="dg-section">
            <h2>10 · Tips &amp; gotchas</h2>
            <ul className="dg-list">
              <li><b>Build image first, words second.</b> Compose Studio is for the picture; the Director is for type + marks.</li>
              <li><b>Save good backgrounds to the library</b> — it compounds: every future card gets faster.</li>
              <li><b>The preview is an instant approximation;</b> the exported file is the source of truth. Tiny differences in wrapping or fonts resolve in the final render.</li>
              <li><b>Cut-outs need a clean source.</b> A subject shot against a busy background can leave ragged edges — the image critic will warn you. Prefer clear, well-lit subjects.</li>
              <li><b>Save-to-Director takes a few seconds</b> (cut-outs are processed server-side) — that's normal.</li>
              <li><b>Nothing is destructive.</b> Crop, recolour, duotone and placement can all be changed or reset later.</li>
            </ul>
            <div className="dg-callout">
              Questions about anything specific you want to achieve? Ask the team — the toolkit is flexible,
              and most "can I do X?" answers are yes.
            </div>
          </section>

          <footer className="dg-footer">
            AskRex · Amplify — designer guide. Built for internal use.
          </footer>
        </main>
      </div>
    </div>
  );
}

const CSS = `
.dg-root{--ink:#0b1430;--ink-soft:#41506e;--brand:#0a22e0;--brand-deep:#0b1e5b;--line:#e6e9f2;--bg:#f6f7fb;--card:#ffffff;
  background:var(--bg);color:var(--ink);min-height:100vh;
  font-family:'Manrope',ui-sans-serif,system-ui,-apple-system,Segoe UI,Roboto,Helvetica,Arial,sans-serif;
  -webkit-font-smoothing:antialiased;}
.dg-root *{box-sizing:border-box;}
.dg-hero{background:linear-gradient(135deg,#0b1e5b 0%,#0a22e0 100%);color:#fff;padding:72px 24px 88px;}
.dg-hero-inner{max-width:920px;margin:0 auto;}
.dg-kicker{font-size:12px;letter-spacing:.22em;font-weight:700;opacity:.8;margin-bottom:18px;}
.dg-hero h1{font-size:clamp(34px,5vw,56px);line-height:1.04;margin:0 0 20px;font-weight:800;letter-spacing:-.02em;}
.dg-lede{font-size:clamp(16px,2.1vw,20px);line-height:1.6;max-width:720px;color:#dfe4ff;margin:0 0 22px;}
.dg-lede strong{color:#fff;}
.dg-hero-meta{font-size:13px;opacity:.7;}
.dg-shell{max-width:1100px;margin:-56px auto 0;padding:0 24px 80px;display:grid;grid-template-columns:230px 1fr;gap:40px;align-items:start;}
.dg-toc{position:sticky;top:24px;background:var(--card);border:1px solid var(--line);border-radius:16px;padding:18px;box-shadow:0 8px 30px rgba(11,30,91,.06);}
.dg-toc-title{font-size:11px;letter-spacing:.14em;text-transform:uppercase;color:var(--ink-soft);font-weight:700;margin-bottom:12px;}
.dg-toc a{display:block;color:var(--ink-soft);text-decoration:none;font-size:14px;padding:7px 0;border-radius:8px;transition:color .15s,padding-left .15s;}
.dg-toc a:hover{color:var(--brand);padding-left:6px;}
.dg-main{background:transparent;min-width:0;}
.dg-section{background:var(--card);border:1px solid var(--line);border-radius:18px;padding:34px 36px;margin-bottom:22px;box-shadow:0 8px 30px rgba(11,30,91,.05);}
.dg-section h2{font-size:26px;font-weight:800;letter-spacing:-.01em;margin:0 0 16px;color:var(--brand-deep);}
.dg-sub{font-size:16px;font-weight:800;margin:26px 0 8px;color:var(--ink);}
.dg-section p{font-size:15.5px;line-height:1.7;color:var(--ink-soft);margin:0 0 14px;}
.dg-section p strong,.dg-section li b{color:var(--ink);}
.dg-term{background:#eef1ff;color:var(--brand-deep);padding:1px 7px;border-radius:6px;font-weight:700;}
.dg-section code{background:#f0f2f8;padding:1px 6px;border-radius:5px;font-size:13px;color:var(--brand-deep);}
.dg-list,.dg-pipeline{margin:0 0 14px;padding-left:0;list-style:none;}
.dg-list li{position:relative;padding:6px 0 6px 22px;font-size:15px;line-height:1.6;color:var(--ink-soft);}
.dg-list li::before{content:"";position:absolute;left:2px;top:13px;width:7px;height:7px;border-radius:2px;background:var(--brand);}
.dg-pipeline li{padding:8px 0 8px 30px;position:relative;font-size:15px;color:var(--ink-soft);}
.dg-pipeline li::before{content:"→";position:absolute;left:4px;top:8px;color:var(--brand);font-weight:800;}
.dg-cards{display:grid;grid-template-columns:1fr 1fr;gap:16px;margin:18px 0;}
.dg-feature{border:1px solid var(--line);border-radius:14px;padding:20px;background:linear-gradient(180deg,#fbfcff,#fff);}
.dg-feature h3{margin:10px 0 8px;font-size:18px;font-weight:800;color:var(--ink);}
.dg-feature p{font-size:14px;margin:0;}
.dg-feature-tag{display:inline-block;font-size:11px;font-weight:700;letter-spacing:.08em;text-transform:uppercase;color:var(--brand);background:#eef1ff;padding:3px 10px;border-radius:999px;}
.dg-feature-tag--alt{color:#0b6b4f;background:#e7f7f0;}
.dg-callout{border-left:4px solid var(--brand);background:#f3f5ff;border-radius:0 12px 12px 0;padding:16px 18px;font-size:14.5px;line-height:1.6;color:var(--ink);margin:18px 0 4px;}
.dg-callout--warn{border-left-color:#e0a008;background:#fff8e8;}
.dg-rows{display:flex;flex-direction:column;gap:2px;margin:6px 0 4px;}
.dg-row{display:grid;grid-template-columns:170px 1fr;gap:18px;padding:14px 0;border-bottom:1px solid var(--line);align-items:start;}
.dg-row:last-child{border-bottom:none;}
.dg-row-h{font-weight:800;font-size:14px;color:var(--brand-deep);}
.dg-row>div:last-child{font-size:14.5px;line-height:1.6;color:var(--ink-soft);}
.dg-steps{counter-reset:s;list-style:none;padding-left:0;margin:6px 0;}
.dg-steps li{counter-increment:s;position:relative;padding:10px 0 10px 44px;font-size:15px;line-height:1.6;color:var(--ink-soft);border-bottom:1px solid var(--line);}
.dg-steps li:last-child{border-bottom:none;}
.dg-steps li::before{content:counter(s);position:absolute;left:0;top:8px;width:28px;height:28px;border-radius:50%;background:var(--brand);color:#fff;font-weight:800;font-size:13px;display:grid;place-items:center;}
.dg-note{font-size:13.5px;color:var(--ink-soft);font-style:italic;background:#fafbff;border:1px dashed var(--line);border-radius:10px;padding:12px 14px;}
.dg-footer{text-align:center;color:var(--ink-soft);font-size:13px;padding:24px 0 4px;}
@media (max-width:860px){.dg-shell{grid-template-columns:1fr;}.dg-toc{position:static;}.dg-cards{grid-template-columns:1fr;}.dg-row{grid-template-columns:1fr;gap:4px;}}
`;
